import handleError from '@fuse/core/errorHandler';
import SimpleDialog from '@fuse/core/SimpleDialog/SimpleDialog';
import FuseSvgIcon from '@fuse/core/FuseSvgIcon/FuseSvgIcon';
import OrganizationsForm from './OrganizationsForm';
import {
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useMessage from 'src/app/hooks/useMessage';
import { Delete, Edit } from '@mui/icons-material';
import FuseLoading from '@fuse/core/FuseLoading';
import PaginationChangeRange from 'app/shared-components/PaginationChangeRange/PaginationChangeRange';
import ConfirmDeleteModalWindow from 'app/shared-components/confirmDeleteModalWindow';
import {
  useGetOrganizationsMutation,
  useDeleteOrganizationsMutation,
} from 'src/app/services/ticketsApi';

const Organizations = () => {
  const { t } = useTranslation('translations');
  const [organizations, setOrganizations] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [currentOrganization, setCurrentOrganization] = useState(null);
  const [isOpenedModal, setIsOpenedModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [params, setParams] = useState({
    filters: '{}',
    first: 0,
    rows: 10,
    sortField: 'id',
    sortOrder: 0,
  });
  const [getSpOrganizations, { isLoading }] = useGetOrganizationsMutation();
  const [deleteOrganization] = useDeleteOrganizationsMutation();
  const { showMessage } = useMessage();

  const handlePageChange = (_e, value) => {
    setPage(value);
    setParams((prev) => ({ ...prev, first: (value - 1) * prev.rows }));
  };

  const handleLimitChange = (newLimit) => {
    setLimit(newLimit);
    setPage(1);
    setParams((prev) => ({ ...prev, rows: newLimit, first: 0 }));
  };

  const showEditDialog = (organization) => {
    setCurrentOrganization(organization);
    setOpenEditDialog(true);
  };

  const showNewItemDialog = () => {
    setCurrentOrganization(null);
    setOpenEditDialog(true);
  };

  const handleOpenDelete = (item) => {
    setSelected(item);
    setIsOpenedModal(true);
  };

  const handleCloseModal = () => {
    setIsOpenedModal(false);
    setSelected(null);
  };

  const handleDelete = (item) => {
    if (!item) return;
    deleteOrganization({ id: item.id })
      .unwrap()
      .then(() => {
        showMessage({ message: t('OrganizationSuccessfullyDeleted'), variant: 'success' });
        handleCloseModal();
        getOrganizations();
      })
      .catch(handleError);
  };

  const getOrganizations = () => {
    getSpOrganizations(params)
      .unwrap()
      .then((res) => {
        setOrganizations(res.items || []);
        const total = Number(res.totalItems || 0);
        const rows = Number(params.rows || limit);
        setTotalPages(rows > 0 ? Math.ceil(total / rows) : 0);
      })
      .catch(handleError);
  };

  useEffect(() => {
    getOrganizations();
  }, [params, limit]);

  if (isLoading) return <FuseLoading />;

  return (
    <div className="flex flex-col flex-1">
      <TableContainer>
        <div className="h-[40px] flex flex-row justify-between items-center dark:bg-dark">
          <h2 className="ml-5">{t('Organizations')}</h2>
          <Button
            className="mx-8"
            size="small"
            variant="outlined"
            color="secondary"
            onClick={showNewItemDialog}
          >
            <FuseSvgIcon size={20}>react-outline:plus</FuseSvgIcon>
            <span className="mx-8">{t('Add')}</span>
          </Button>
        </div>
        <Table style={{ overflow: 'auto' }}>
          <TableHead>
            <TableRow className="dark:bg-dark">
              <TableCell>{t('Name')}</TableCell>
              <TableCell>{t('ShortName')}</TableCell>
              <TableCell>{t('WorkAddress')}</TableCell>
              <TableCell colSpan={2}>{t('Inn')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody className="h-[1000px] overflow-y-auto">
            {organizations?.map((item) => (
              <TableRow key={item.id} className="bg-white dark:bg-dark hover:bg-grey-200">
                <TableCell>{item.title}</TableCell>
                <TableCell>{item.shortName}</TableCell>
                <TableCell>{item.workAddress}</TableCell>
                <TableCell>{item.inn}</TableCell>
                <TableCell>
                  <div className="flex">
                    <IconButton
                      edge="end"
                      aria-label="edit"
                      className="mr-5"
                      color="info"
                      onClick={() => showEditDialog(item)}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      className="mr-10"
                      color="error"
                      onClick={() => handleOpenDelete(item)}
                    >
                      <Delete />
                    </IconButton>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <SimpleDialog
          title={currentOrganization ? t('EditOrganization') : t('AddOrganization')}
          open={openEditDialog}
          onClose={() => setOpenEditDialog(false)}
        >
          <OrganizationsForm
            position={currentOrganization}
            refetchList={getOrganizations}
            onClose={() => setOpenEditDialog(false)}
            isEdit={!!currentOrganization}
          />
        </SimpleDialog>
      </TableContainer>
      <div className="flex items-center justify-between px-5">
        <PaginationChangeRange value={limit} setValue={handleLimitChange} />
        <Pagination
          className="flex items-center justify-center mt-10"
          count={totalPages}
          page={page}
          onChange={handlePageChange}
        />
        <div className="md:w-[100px]" />
      </div>
      {isOpenedModal && (
        <ConfirmDeleteModalWindow
          onClose={handleCloseModal}
          setIsOpenModal={setIsOpenedModal}
          modalTitle={'AreYouSureToDeleteOrganization'}
          deleteMessage={() => handleDelete(selected)}
          open={isOpenedModal}
        />
      )}
    </div>
  );
};

export default Organizations;
